/* eslint-disable no-unused-vars */
/* eslint-disable arrow-body-style */
import React, { useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardHeader, Button, Alert, Badge, Spinner,
} from 'reactstrap';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { get, post } from '../../helpers/api_helper';

const OUTBOUND_DATA_TYPES = [
  { key: 'itemsku', label: 'Item SKU' },
  { key: 'pricing', label: 'Catalog Pricing' },
  { key: 'availability', label: 'Catalog Availability' },
];

function getPrivateKeyPathLabel(connection, t) {
  if (connection?.privateKey?.path) return connection.privateKey.path;
  if (connection?.privateKey?.source === 'config-value') return t('Injected configuration value');
  return t('Not configured');
}

function getPrivateKeyReadableLabel(connection, t) {
  if (connection?.privateKey?.readable === null) return t('Not applicable');
  return connection?.privateKey?.readable ? t('Yes') : t('No');
}

function getRemoteVerificationLabel(verification, t) {
  switch (verification?.status) {
    case 'verified': return t('Visible and size verified');
    case 'not-visible': return t('Not visible after acknowledgement');
    case 'size-mismatch': return t('Visible, but size did not match');
    case 'unavailable': return t('Check unavailable');
    case 'unsupported': return t('Check unsupported');
    default: return t('Not checked');
  }
}

function BestBuy(props) {
  const { t } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const [testResponse, setTestResponse] = useState(null);
  const [symphonyResponse, setSymphonyResponse] = useState(null);
  const [symphonyTesting, setSymphonyTesting] = useState(false);
  const [outboundCount, setOutboundCount] = useState(3);
  const [outboundResponse, setOutboundResponse] = useState(null);
  const [outboundSendingType, setOutboundSendingType] = useState(null);

  const testNeeds = async () => {
    try {
      const response = await get('/api/BestBuy/needs');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testPOs = async () => {
    try {
      const response = await get('/api/BestBuy/pos');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testShipments = async () => {
    try {
      const response = await get('/api/BestBuy/shipments');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testCancels = async () => {
    try {
      const response = await get('/api/BestBuy/cancels');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testConcludes = async () => {
    try {
      const response = await get('/api/BestBuy/concludes');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testInvoice = async () => {
    try {
      const response = await get('/api/BestBuy/invoice/150535');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testRGMs = async () => {
    try {
      const response = await get('/api/BestBuy/rgms');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const test = async () => {
    try {
      const response = await get('/api/BestBuy/test');

      setTestResponse(response);
    } catch (error) {
      toastr.error(t('Error fetching remote data'), t('Error'));
    }
  };

  const testSymphony = async () => {
    setSymphonyTesting(true);
    setSymphonyResponse(null);

    try {
      const response = await get('/api/BestBuy/symphony/test');

      setSymphonyResponse(response);
    } catch (error) {
      setSymphonyResponse(error.response?.data || {
        ok: false,
        error: {
          category: 'request',
          message: error.message,
        },
      });
    } finally {
      setSymphonyTesting(false);
    }
  };

  const sendSymphonySample = async (dataType) => {
    const requestedCount = Number(outboundCount);
    const count = Number.isFinite(requestedCount)
      ? Math.min(10, Math.max(1, Math.trunc(requestedCount)))
      : 3;
    const type = OUTBOUND_DATA_TYPES.find((candidate) => candidate.key === dataType);
    // This is an intentional last guard before the utility performs a real SFTP upload.
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(t(
      'Upload a real {{type}} file containing up to {{count}} due listings to Best Buy Symphony?',
      { count, type: type?.label || dataType },
    ));
    if (!confirmed) return;

    setOutboundCount(count);
    setOutboundSendingType(dataType);
    setOutboundResponse(null);

    try {
      const response = await post(`/api/BestBuy/symphony/outbound/test-send/${dataType}`, {
        count,
      });
      setOutboundResponse(response);
    } catch (error) {
      const response = error.response?.data || {
        complete: false,
        error: {
          code: error.code || null,
          message: error.message,
        },
      };
      setOutboundResponse(response);
      toastr.error(
        response.error?.message || response.failures?.[0]?.message || t('Symphony send failed'),
        t('Error'),
      );
    } finally {
      setOutboundSendingType(null);
    }
  };

  const outboundBatch = outboundResponse?.batches?.[0] || null;
  const outboundFailure = outboundResponse?.error || outboundResponse?.failures?.[0] || null;
  const outboundArtifact = outboundBatch || outboundFailure?.artifact || null;
  const outboundReceipt = outboundBatch || outboundFailure?.uploadReceipt || null;
  let outboundAlertColor = 'info';
  if (outboundBatch?.sent) outboundAlertColor = 'success';
  if (outboundBatch?.remoteVerification
    && outboundBatch.remoteVerification.status !== 'verified') {
    outboundAlertColor = 'warning';
  }
  if (outboundBatch?.remoteVerification?.status === 'size-mismatch') {
    outboundAlertColor = 'danger';
  }
  if (outboundFailure) outboundAlertColor = 'danger';

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="7">
            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Test triggers for Fidelitone SFTP')}</CardHeader>
              <CardBody>
                <div className="hstack gap-2">
                  <Button color="primary" size="sm" onClick={testNeeds}>
                    {t('Test Needs')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testPOs}>
                    {t('Test POs')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testShipments}>
                    {t('Test Shipments')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testCancels}>
                    {t('Test Cancels')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testConcludes}>
                    {t('Test Concludes')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testInvoice}>
                    {t('Test Invoice')}
                  </Button>

                  <Button color="primary" size="sm" onClick={testRGMs}>
                    {t('Test RGMs')}
                  </Button>

                  <Button color="primary" size="sm" onClick={test}>
                    {t('Test')}
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-transparent border-bottom d-flex align-items-center justify-content-between">
                <span>{t('Symphony SFTP diagnostics')}</span>
                {symphonyResponse && (
                  <Badge color={symphonyResponse.ok ? 'success' : 'danger'}>
                    {symphonyResponse.ok ? t('Connected') : t('Failed')}
                  </Badge>
                )}
              </CardHeader>
              <CardBody>
                <p className="text-muted">
                  {t('Runs a read-only connection test and lists XML files in the configured Symphony inbox.')}
                </p>

                <Button
                  color="primary"
                  size="sm"
                  disabled={symphonyTesting}
                  onClick={testSymphony}
                >
                  {symphonyTesting && <Spinner size="sm" className="me-2" />}
                  {symphonyTesting ? t('Testing Connection') : t('Test Symphony Connection')}
                </Button>

                {symphonyResponse && (
                  <>
                    <Alert
                      color={symphonyResponse.ok ? 'success' : 'danger'}
                      className="mt-3 mb-3"
                    >
                      <div className="fw-semibold">
                        {symphonyResponse.ok
                          ? t('Connection succeeded and the inbox is readable.')
                          : t('Connection test failed.')}
                      </div>
                      {!symphonyResponse.ok && symphonyResponse.error && (
                        <div>
                          <span className="text-uppercase me-2">
                            {symphonyResponse.error.category}
                          </span>
                          {symphonyResponse.error.message}
                        </div>
                      )}
                    </Alert>

                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-3">
                        <tbody>
                          <tr>
                            <th scope="row">{t('Host')}</th>
                            <td>{symphonyResponse.connection?.host || t('Not configured')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Port')}</th>
                            <td>{symphonyResponse.connection?.port || 22}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Username')}</th>
                            <td>{symphonyResponse.connection?.username || t('Not configured')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Authentication')}</th>
                            <td>{symphonyResponse.connection?.authentication || t('Unknown')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Address Family')}</th>
                            <td>{symphonyResponse.connection?.addressFamily || t('Automatic')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Private Key Path')}</th>
                            <td>
                              <code>
                                {getPrivateKeyPathLabel(symphonyResponse.connection, t)}
                              </code>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Private Key Readable')}</th>
                            <td>{getPrivateKeyReadableLabel(symphonyResponse.connection, t)}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Handshake Timeout')}</th>
                            <td>{`${symphonyResponse.connection?.readyTimeoutMs || 60000} ms`}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Inbox')}</th>
                            <td>{symphonyResponse.connection?.inboundPath || t('Not configured')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Outbox')}</th>
                            <td>{symphonyResponse.connection?.outboundPath || t('Not configured')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('PGP Decryption')}</th>
                            <td>
                              {symphonyResponse.connection?.pgp?.decryptionReady
                                ? t('Configured via {{source}}', {
                                  source: symphonyResponse.connection.pgp.privateKeySource,
                                })
                                : t('Not configured')}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t('PGP Passphrase')}</th>
                            <td>
                              {symphonyResponse.connection?.pgp?.passphraseConfigured
                                ? t('Configured')
                                : t('Not configured or not required')}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Inbound Files')}</th>
                            <td>{symphonyResponse.inbox?.fileCount ?? t('Unavailable')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Duration')}</th>
                            <td>{`${symphonyResponse.durationMs ?? 0} ms`}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Checked')}</th>
                            <td>
                              {symphonyResponse.checkedAt
                                ? new Date(symphonyResponse.checkedAt).toLocaleString()
                                : t('Unknown')}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Service Operation')}</th>
                            <td><code>{symphonyResponse.connection?.serviceOperation}</code></td>
                          </tr>
                          <tr>
                            <th scope="row">{t('Equivalent Debug Command')}</th>
                            <td>
                              <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {symphonyResponse.connection?.equivalentCommand}
                              </code>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {symphonyResponse.inbox?.files?.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-2">{t('Inbox files')}</div>
                        <ul className="mb-0">
                          {symphonyResponse.inbox.files.map((file) => (
                            <li key={file.remotePath}>
                              <code>{file.name}</code>
                              {Number.isFinite(file.size) && ` (${file.size.toLocaleString()} bytes)`}
                            </li>
                          ))}
                        </ul>
                        {symphonyResponse.inbox.truncated && (
                          <div className="text-muted mt-2">{t('Only the first 25 files are shown.')}</div>
                        )}
                      </div>
                    )}

                    {symphonyResponse.sshTrace?.length > 0 && (
                      <div className="mt-3">
                        <div className="fw-semibold mb-2">{t('Node SSH trace')}</div>
                        <pre
                          className="border rounded p-2"
                          style={{
                            backgroundColor: '#111827',
                            borderColor: '#4b5563',
                            color: '#e5e7eb',
                            maxHeight: '24rem',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {symphonyResponse.sshTrace.join('\n')}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-transparent border-bottom d-flex align-items-center justify-content-between">
                <span>{t('Symphony outbound test sends')}</span>
                {outboundBatch && (
                  <Badge color={outboundBatch.sent ? 'success' : 'warning'}>
                    {outboundBatch.sent ? t('Uploaded') : t('Not sent')}
                  </Badge>
                )}
              </CardHeader>
              <CardBody>
                <p className="text-muted">
                  {t('Builds a real XML file from a few due listings, uploads it to the configured Symphony outbox, and records each listing as sent only after SFTP confirms the upload.')}
                </p>

                <div className="d-flex flex-wrap align-items-end gap-2">
                  <div>
                    <label className="form-label mb-1" htmlFor="symphony-outbound-count">
                      {t('Maximum records')}
                    </label>
                    <input
                      id="symphony-outbound-count"
                      type="number"
                      min="1"
                      max="10"
                      className="form-control form-control-sm"
                      style={{ width: '7rem' }}
                      disabled={Boolean(outboundSendingType)}
                      value={outboundCount}
                      onChange={(event) => setOutboundCount(event.target.value)}
                    />
                  </div>

                  {OUTBOUND_DATA_TYPES.map((dataType) => (
                    <Button
                      key={dataType.key}
                      color="primary"
                      size="sm"
                      disabled={Boolean(outboundSendingType)}
                      onClick={() => sendSymphonySample(dataType.key)}
                    >
                      {outboundSendingType === dataType.key && <Spinner size="sm" className="me-2" />}
                      {outboundSendingType === dataType.key
                        ? t('Sending {{type}}', { type: dataType.label })
                        : t('Send {{type}} Sample', { type: dataType.label })}
                    </Button>
                  ))}
                </div>

                {outboundResponse && (
                  <div className="mt-3">
                    <Alert color={outboundAlertColor}>
                      {outboundBatch?.sent && (
                        <div>
                          <div className="fw-semibold">{t('Test file uploaded successfully.')}</div>
                          <div>{outboundBatch.message}</div>
                        </div>
                      )}
                      {!outboundBatch && !outboundFailure && (
                        <div>
                          <div className="fw-semibold">{t('Nothing was sent.')}</div>
                          <div>{t('No eligible listings currently have this data type due.')}</div>
                        </div>
                      )}
                      {outboundFailure && (
                        <div>
                          <div className="fw-semibold">{t('Test send failed.')}</div>
                          <div>
                            {outboundFailure.code && (
                              <span className="text-uppercase me-2">{outboundFailure.code}</span>
                            )}
                            {outboundFailure.message}
                          </div>
                          {outboundFailure.stage && (
                            <div>{t('Stage: {{stage}}', { stage: outboundFailure.stage })}</div>
                          )}
                          {outboundFailure.field && (
                            <div>{t('Field: {{field}}', { field: outboundFailure.field })}</div>
                          )}
                          {outboundFailure.listingId && (
                            <div>
                              {t('Listing ID: {{listingId}}', {
                                listingId: outboundFailure.listingId,
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </Alert>

                    {outboundArtifact && (
                      <>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered mb-3">
                            <tbody>
                              <tr>
                                <th scope="row">{t('Data Type')}</th>
                                <td>{outboundArtifact.dataType}</td>
                              </tr>
                              <tr>
                                <th scope="row">{t('Records')}</th>
                                <td>{outboundArtifact.recordCount}</td>
                              </tr>
                              <tr>
                                <th scope="row">{t('SKUs')}</th>
                                <td>{outboundArtifact.skus?.join(', ') || t('Unavailable')}</td>
                              </tr>
                              <tr>
                                <th scope="row">{t('Transmission ID')}</th>
                                <td><code>{outboundArtifact.transmissionId}</code></td>
                              </tr>
                              <tr>
                                <th scope="row">{t('Filename')}</th>
                                <td><code>{outboundArtifact.preparedFilename}</code></td>
                              </tr>
                              {outboundReceipt?.stagedRemotePath && (
                                <tr>
                                  <th scope="row">{t('Staging Path')}</th>
                                  <td><code>{outboundReceipt.stagedRemotePath}</code></td>
                                </tr>
                              )}
                              {outboundReceipt?.stagedAcknowledgedAt && (
                                <tr>
                                  <th scope="row">{t('Staging Upload')}</th>
                                  <td>{t('Acknowledged by remote server')}</td>
                                </tr>
                              )}
                              {outboundReceipt?.stagedAcknowledgedAt && (
                                <tr>
                                  <th scope="row">{t('Staging Acknowledged At')}</th>
                                  <td>
                                    {new Date(
                                      outboundReceipt.stagedAcknowledgedAt,
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {outboundReceipt?.stagingVerification && (
                                <tr>
                                  <th scope="row">{t('Staging Remote Check')}</th>
                                  <td>
                                    {getRemoteVerificationLabel(
                                      outboundReceipt.stagingVerification,
                                      t,
                                    )}
                                    {' — '}
                                    {outboundReceipt.stagingVerification.message}
                                  </td>
                                </tr>
                              )}
                              {outboundReceipt?.remotePath && (
                                <tr>
                                  <th scope="row">{t('Final Remote Path')}</th>
                                  <td><code>{outboundReceipt.remotePath}</code></td>
                                </tr>
                              )}
                              {outboundReceipt?.renamedAt && (
                                <tr>
                                  <th scope="row">{t('Remote Rename')}</th>
                                  <td>{t('Acknowledged by remote server')}</td>
                                </tr>
                              )}
                              {outboundReceipt?.renamedAt && (
                                <tr>
                                  <th scope="row">{t('Renamed At')}</th>
                                  <td>{new Date(outboundReceipt.renamedAt).toLocaleString()}</td>
                                </tr>
                              )}
                              {Number.isFinite(outboundReceipt?.bytes) && (
                                <tr>
                                  <th scope="row">{t('Uploaded Bytes')}</th>
                                  <td>{outboundReceipt.bytes.toLocaleString()}</td>
                                </tr>
                              )}
                              {outboundReceipt?.remoteVerification && (
                                <>
                                  <tr>
                                    <th scope="row">{t('Immediate Remote Check')}</th>
                                    <td>
                                      {getRemoteVerificationLabel(
                                        outboundReceipt.remoteVerification,
                                        t,
                                      )}
                                    </td>
                                  </tr>
                                  {outboundReceipt.remoteVerification.checkedAt && (
                                    <tr>
                                      <th scope="row">{t('Remote Checked At')}</th>
                                      <td>
                                        {new Date(
                                          outboundReceipt.remoteVerification.checkedAt,
                                        ).toLocaleString()}
                                      </td>
                                    </tr>
                                  )}
                                  {Number.isFinite(
                                    outboundReceipt.remoteVerification.remoteBytes,
                                  ) && (
                                    <tr>
                                      <th scope="row">{t('Remote Bytes')}</th>
                                      <td>
                                        {outboundReceipt.remoteVerification.remoteBytes
                                          .toLocaleString()}
                                      </td>
                                    </tr>
                                  )}
                                  {outboundReceipt.remoteVerification.modifiedAt && (
                                    <tr>
                                      <th scope="row">{t('Remote Modified')}</th>
                                      <td>
                                        {new Date(
                                          outboundReceipt.remoteVerification.modifiedAt,
                                        ).toLocaleString()}
                                      </td>
                                    </tr>
                                  )}
                                  <tr>
                                    <th scope="row">{t('Remote Check Details')}</th>
                                    <td>{outboundReceipt.remoteVerification.message}</td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {outboundArtifact.contents && (
                          <div>
                            <div className="fw-semibold mb-2">{t('Uploaded XML')}</div>
                            <pre
                              className="border rounded p-2 mb-0"
                              style={{
                                backgroundColor: '#111827',
                                borderColor: '#4b5563',
                                color: '#e5e7eb',
                                maxHeight: '32rem',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {outboundArtifact.contents}
                            </pre>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

BestBuy.defaultProps = {
  t: (x) => (x),
};

BestBuy.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(BestBuy));
